import {
  type Category,
  MIN_SLA_DAYS,
  type Priority,
  STAGE_META,
  STAGES,
  type Stage,
  type Status,
} from '@hrms/shared'
import { ColorSelect, Select, TextInput } from '../../../components/ui/Field.tsx'
import { hint } from '../../../lib/hint.ts'
import { GridRow, RowActions, SettingsGrid } from './SettingsGrid.tsx'

interface Item {
  id: string
  name: string
  archived: boolean
}

export interface ListSectionProps<T extends Item> {
  items: T[]
  /** Ids already saved on the server (others can simply be removed). */
  savedIds: Set<string>
  onChange: (index: number, patch: Partial<T>) => void
  onMove: (index: number, dir: -1 | 1) => void
  onRemove: (index: number) => void
}

const COLOR_HINT = 'Colour for chips, lozenges and the board. Each one is tuned for light and dark.'

function NameInput({
  item,
  onChange,
  noun,
}: {
  item: Item
  onChange: (v: string) => void
  noun: string
}) {
  return (
    <TextInput
      size="sm"
      data-focus-id={item.id}
      aria-label={`${noun} name`}
      value={item.name}
      maxLength={80}
      invalid={!item.name.trim()}
      placeholder="Name"
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

function actions<T extends Item>(p: ListSectionProps<T>, item: T, index: number) {
  return (
    <RowActions
      name={item.name}
      index={index}
      count={p.items.length}
      archived={item.archived}
      isNew={!p.savedIds.has(item.id)}
      onMove={(dir) => p.onMove(index, dir)}
      onArchive={(archived) => p.onChange(index, { archived } as Partial<T>)}
      onRemove={() => p.onRemove(index)}
    />
  )
}

const CATEGORY_COLS = 'grid-cols-[minmax(150px,1fr)_140px_minmax(240px,2fr)_92px]'

export function CategoriesSection(p: ListSectionProps<Category>) {
  return (
    <SettingsGrid
      label="Categories"
      columns={CATEGORY_COLS}
      minWidth="min-w-[680px]"
      headers={[
        { label: 'Name' },
        { label: 'Colour', hint: COLOR_HINT },
        {
          label: 'Prompt shown to employees',
          hint: 'Becomes the description placeholder when an employee picks this category.',
        },
        { label: '' },
      ]}
    >
      {p.items.map((c, i) => (
        <GridRow
          key={c.id}
          columns={CATEGORY_COLS}
          archived={c.archived}
          cells={[
            <NameInput
              key="n"
              item={c}
              noun="Category"
              onChange={(name) => p.onChange(i, { name })}
            />,
            <ColorSelect
              key="c"
              size="sm"
              aria-label={`${c.name || 'Category'} colour`}
              value={c.color}
              onChange={(color) => p.onChange(i, { color })}
            />,
            <TextInput
              key="h"
              size="sm"
              aria-label={`${c.name || 'Category'} prompt shown to employees`}
              value={c.hint}
              maxLength={500}
              placeholder="e.g. What happened, and when?"
              onChange={(e) => p.onChange(i, { hint: e.target.value })}
            />,
          ]}
          actions={actions(p, c, i)}
        />
      ))}
    </SettingsGrid>
  )
}

const PRIORITY_COLS = 'grid-cols-[minmax(150px,1fr)_140px_170px_92px]'

export function PrioritiesSection(p: ListSectionProps<Priority>) {
  return (
    <SettingsGrid
      label="Priorities"
      columns={PRIORITY_COLS}
      minWidth="min-w-[600px]"
      headers={[
        { label: 'Name' },
        { label: 'Colour', hint: COLOR_HINT },
        {
          label: 'Resolve within (days)',
          hint: 'Calendar days from when a ticket is raised to its default ETA. Steps of 0.5, minimum 0.5. Applies to new tickets and future recalculations.',
        },
        { label: '' },
      ]}
    >
      {p.items.map((pr, i) => (
        <GridRow
          key={pr.id}
          columns={PRIORITY_COLS}
          archived={pr.archived}
          cells={[
            <NameInput
              key="n"
              item={pr}
              noun="Priority"
              onChange={(name) => p.onChange(i, { name })}
            />,
            <ColorSelect
              key="c"
              size="sm"
              aria-label={`${pr.name || 'Priority'} colour`}
              value={pr.color}
              onChange={(color) => p.onChange(i, { color })}
            />,
            <TextInput
              key="d"
              size="sm"
              type="number"
              inputMode="decimal"
              min={MIN_SLA_DAYS}
              step={0.5}
              aria-label={`${pr.name || 'Priority'} resolve within days`}
              className="tabular"
              value={Number.isFinite(pr.slaDays) ? pr.slaDays : ''}
              onChange={(e) =>
                p.onChange(i, {
                  slaDays: e.target.value === '' ? Number.NaN : Number(e.target.value),
                })
              }
            />,
          ]}
          actions={actions(p, pr, i)}
        />
      ))}
    </SettingsGrid>
  )
}

const STATUS_COLS = 'grid-cols-[minmax(150px,1fr)_140px_170px_92px]'

const STAGE_HINT = STAGES.map((s) => `${STAGE_META[s].label}: ${STAGE_META[s].description}`).join(
  '\n',
)

export function StatusesSection(p: ListSectionProps<Status>) {
  return (
    <SettingsGrid
      label="Statuses"
      columns={STATUS_COLS}
      minWidth="min-w-[600px]"
      headers={[
        { label: 'Name' },
        { label: 'Colour', hint: COLOR_HINT },
        { label: 'Stage', hint: STAGE_HINT },
        { label: '' },
      ]}
    >
      {p.items.map((s, i) => (
        <GridRow
          key={s.id}
          columns={STATUS_COLS}
          archived={s.archived}
          cells={[
            <NameInput
              key="n"
              item={s}
              noun="Status"
              onChange={(name) => p.onChange(i, { name })}
            />,
            <ColorSelect
              key="c"
              size="sm"
              aria-label={`${s.name || 'Status'} colour`}
              value={s.color}
              onChange={(color) => p.onChange(i, { color })}
            />,
            <Select
              key="s"
              size="sm"
              aria-label={`${s.name || 'Status'} stage`}
              data-tour="settings-stage"
              value={s.stage}
              onChange={(e) => p.onChange(i, { stage: e.target.value as Stage })}
              {...hint(STAGE_META[s.stage].description)}
            >
              {STAGES.map((st) => (
                <option key={st} value={st}>
                  {STAGE_META[st].label}
                </option>
              ))}
            </Select>,
          ]}
          actions={actions(p, s, i)}
        />
      ))}
    </SettingsGrid>
  )
}
